import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import TextField from "@/common/components/TextField";
import { InfoIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import useBlockEstablishment from "../../hooks/useBlockEstablishment";
import useUnblockEstablishment from "../../hooks/useUnblockEstablishment";
import { establishmentBlockSchema, EstablishmentBlockValidationSchema, establishmentUnblockSchema, EstablishmentUnblockValidationSchema } from "../../schemas/blockUnblockEstablishment.schema";

interface BlockUnblockEstablishmentProps {
  establishment?: EstablishmentCompanyData;
  disableButtons?: boolean;
}

const BlockUnblockEstablishment = ({ establishment, disableButtons }: BlockUnblockEstablishmentProps) => {
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [blockModalIsOpen, setBlockModalIsOpen] = useState(false);
  const [unblockModalIsOpen, setUnblockModalIsOpen] = useState(false);

  const { mutate: blockEstablishment } = useBlockEstablishment({
    establishmentId: establishment?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishment?.id] });
      blockFormReset();
      setBlockModalIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError: blockFormSetError })(error);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const { mutate: unblockEstablishment } = useUnblockEstablishment({
    establishmentId: establishment?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishment?.id] });
      unblockFormReset();
      setUnblockModalIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError: unblockFormSetError })(error);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const isBlocked = establishment?.status === 'blocked';
  const isDisabled = establishment?.status === 'disabled';

  const {
    register: blockFormRegister,
    reset: blockFormReset,
    setError: blockFormSetError,
    handleSubmit: blockFormHandleSubmit,
    formState: { errors: blockFormErrors },
  } = useForm<EstablishmentBlockValidationSchema>({
    resolver: zodResolver(establishmentBlockSchema),
  });

  const {
    register: unblockFormRegister,
    reset: unblockFormReset,
    setError: unblockFormSetError,
    handleSubmit: unblockFormHandleSubmit,
    formState: { errors: unblockFormErrors },
  } = useForm<EstablishmentUnblockValidationSchema>({
    resolver: zodResolver(establishmentUnblockSchema),
  });

  return (
    <>
      {!isDisabled && (
        <div className={twMerge("flex w-full justify-between items-center bg-white-100 rounded-lg p-2", isBlocked && 'bg-[#FCCDCA33]')}>
          <div className="flex items-center gap-x-2">
            <InfoIcon strokeWidth={1} className="text-yellow-600" />

            <div>
              <p className="font-bold text-[10px] leading-3">
                {!isBlocked ? (
                  'Bloqueio da empresa impede todas transações.'
                ) : (
                  'Desbloqueie a empresa para permitir as transações.'
                )}
              </p>

              <p className="font-light text-[10px] italic leading-3">
                {!isBlocked ? (
                  'Caso a empresa seja bloqueada, nenhuma transação poderá ser feita pela empresa.'
                ) : (
                  'Se a empresa for desbloqueada, transações poderão ser feitas.'
                )}
              </p>
            </div>
          </div>

          {!isBlocked ? (
            <Modal
              triggerDisabled={disableButtons}
              Trigger={(
                <Button operationKlassToCheckPermission="Admin::Establishment::Companies::Block" type="button" variant="yellow-black" disabled={disableButtons}>
                  Bloquear estabelecimento
                </Button>
              )}
              title="Você tem certeza?"
              subtitle="Caso a empresa seja bloqueada, nenhuma transação poderá ser feita pela empresa."
              subtitleClassName="mt-1 text-white-300"
              showClose={false}
              useCancellableFooter
              isOpen={blockModalIsOpen}
              setIsOpen={setBlockModalIsOpen}
              onConfirm={blockFormHandleSubmit((data) => blockEstablishment(data))}
              body={(
                <form className="flex flex-col gap-4">
                  <TextField
                    className="text-white-800 opacity-80"
                    label="Motivo"
                    {...blockFormRegister('block_reason')}
                    errorMessage={blockFormErrors.block_reason?.message || undefined}
                  />
                </form>
              )}
            />
          ) : (
            <Modal
              triggerDisabled={disableButtons}
              Trigger={(
                <Button operationKlassToCheckPermission="Admin::Establishment::Companies::Unblock" type="button" disabled={disableButtons}>
                  Desbloquear estabelecimento
                </Button>
              )}
              title="Você tem certeza?"
              subtitle="Se a empresa for desbloqueada, transações poderão ser feitas."
              subtitleClassName="mt-1 text-white-300"
              showClose={false}
              useCancellableFooter
              isOpen={unblockModalIsOpen}
              setIsOpen={setUnblockModalIsOpen}
              onConfirm={unblockFormHandleSubmit((data) => unblockEstablishment(data))}
              body={(
                <form className="flex flex-col gap-4">
                  <TextField
                    className="text-white-800 opacity-80"
                    label="Motivo"
                    {...unblockFormRegister('unblock_reason')}
                    errorMessage={unblockFormErrors.unblock_reason?.message || undefined}
                  />
                </form>
              )}
            />
          )}
        </div>
      )}

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
};

export default BlockUnblockEstablishment;
